const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

const STATUS_CLASSES = {
  pendiente: 'is-pending',
  en_progreso: 'is-in-progress',
  bloqueada: 'is-blocked',
  completada: 'is-completed',
  cancelada: 'is-cancelled',
};

const TYPE_CLASSES = {
  actividad: 'is-activity',
  etapa: 'is-stage',
  hito: 'is-milestone',
};

const taskKey = id => String(id);

export const isValidDateOnly = value => {
  const match = DATE_ONLY_PATTERN.exec(String(value || ''));
  if (!match) return false;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(Date.UTC(year, month - 1, day));
  return date.getUTCFullYear() === year
    && date.getUTCMonth() === month - 1
    && date.getUTCDate() === day;
};

const normalizeDependencyIds = value => {
  if (value == null || value === '') return [];
  let source = value;
  if (typeof source === 'string') {
    const trimmed = source.trim();
    if (!trimmed) return [];
    try {
      source = JSON.parse(trimmed);
    } catch (_) {
      source = trimmed.split(',');
    }
  }
  if (!Array.isArray(source)) source = [source];
  return [...new Set(source
    .map(item => item && typeof item === 'object' ? (item.task_id ?? item.id) : item)
    .filter(item => item !== null && item !== undefined && String(item).trim() !== '')
    .map(item => taskKey(item).trim()))];
};

const dependencyCycle = dependencyMap => {
  const visiting = new Set();
  const visited = new Set();
  const visit = key => {
    if (visiting.has(key)) return true;
    if (visited.has(key)) return false;
    visiting.add(key);
    if ((dependencyMap.get(key) || []).some(visit)) return true;
    visiting.delete(key);
    visited.add(key);
    return false;
  };
  return [...dependencyMap.keys()].some(visit);
};

const taskDepth = (task, tasksById, visited = new Set()) => {
  const parentKey = task?.parent_task_id == null ? null : taskKey(task.parent_task_id);
  if (!parentKey || !tasksById.has(parentKey) || visited.has(parentKey)) return 0;
  return 1 + taskDepth(tasksById.get(parentKey), tasksById, new Set(visited).add(parentKey));
};

const normalizedProgress = (task, issues) => {
  const value = Number(task.progress_percentage);
  if (!Number.isFinite(value)) {
    issues.push({ code: 'INVALID_PROGRESS', taskId: task.id, message: `La tarea “${task.title || task.id}” no tiene un avance válido; se mostrará en 0 %.` });
    return 0;
  }
  if (value < 0 || value > 100) {
    issues.push({ code: 'PROGRESS_OUT_OF_RANGE', taskId: task.id, message: `La tarea “${task.title || task.id}” tiene un avance fuera de 0–100 %; se ajustará sólo para visualizarla.` });
  }
  return Math.min(100, Math.max(0, value));
};

export const adaptProjectTasksToGantt = input => {
  const sourceTasks = Array.isArray(input) ? input.filter(Boolean) : [];
  const issues = [];
  const tasksById = new Map();

  sourceTasks.forEach(task => {
    if (task.id == null || String(task.id).trim() === '') {
      issues.push({ code: 'MISSING_TASK_ID', taskId: null, message: `La tarea “${task.title || 'Sin título'}” no tiene identificador y no puede mostrarse.` });
      return;
    }
    const key = taskKey(task.id);
    if (tasksById.has(key)) {
      issues.push({ code: 'DUPLICATE_TASK_ID', taskId: task.id, message: `El identificador de la tarea “${task.title || task.id}” está duplicado.` });
      return;
    }
    tasksById.set(key, task);
  });

  const displayableKeys = new Set();
  const unscheduledTasks = [];
  tasksById.forEach((task, key) => {
    const startValid = isValidDateOnly(task.start_date);
    const endValid = isValidDateOnly(task.due_date);
    let reason = '';
    if (!startValid || !endValid) reason = 'Completá las fechas de inicio y vencimiento.';
    else if (task.due_date < task.start_date) reason = 'La fecha de vencimiento es anterior al inicio.';
    if (reason) {
      unscheduledTasks.push({ task, reason });
      issues.push({ code: startValid && endValid ? 'INVALID_DATE_RANGE' : 'INCOMPLETE_TASK_DATES', taskId: task.id, message: `La tarea “${task.title || task.id}” no aparece en el cronograma. ${reason}` });
    } else displayableKeys.add(key);

    if (task.parent_task_id != null && !tasksById.has(taskKey(task.parent_task_id))) {
      issues.push({ code: 'MISSING_PARENT_TASK', taskId: task.id, message: `La tarea “${task.title || task.id}” referencia una etapa padre que no existe.` });
    }
  });

  const dependencyMap = new Map();
  displayableKeys.forEach(key => {
    const task = tasksById.get(key);
    const validDependencies = normalizeDependencyIds(task.dependency_ids).filter(dependencyId => {
      if (dependencyId === key) {
        issues.push({ code: 'SELF_DEPENDENCY', taskId: task.id, message: `La tarea “${task.title || task.id}” no puede depender de sí misma.` });
        return false;
      }
      if (!tasksById.has(dependencyId)) {
        issues.push({ code: 'MISSING_DEPENDENCY', taskId: task.id, message: `La tarea “${task.title || task.id}” referencia una dependencia que no existe.` });
        return false;
      }
      if (!displayableKeys.has(dependencyId)) {
        issues.push({ code: 'UNSCHEDULED_DEPENDENCY', taskId: task.id, message: `Una dependencia de “${task.title || task.id}” no tiene fechas completas y no puede dibujarse.` });
        return false;
      }
      return true;
    });
    dependencyMap.set(key, validDependencies);
  });

  const hasDependencyCycle = dependencyCycle(dependencyMap);
  if (hasDependencyCycle) {
    issues.push({ code: 'DEPENDENCY_CYCLE', taskId: null, message: 'Las tareas contienen dependencias circulares. Las flechas se ocultarán hasta corregirlas.' });
  }

  const ganttTasks = [...displayableKeys]
    .map(key => tasksById.get(key))
    .sort((left, right) => {
      const byDepth = taskDepth(left, tasksById) - taskDepth(right, tasksById);
      if (byDepth) return byDepth;
      const byStart = String(left.start_date).localeCompare(String(right.start_date));
      if (byStart) return byStart;
      return String(left.title || '').localeCompare(String(right.title || ''), 'es');
    })
    .map(task => {
      const key = taskKey(task.id);
      const status = STATUS_CLASSES[task.status] || STATUS_CLASSES.pendiente;
      const type = TYPE_CLASSES[task.task_type] || TYPE_CLASSES.actividad;
      const depth = taskDepth(task, tasksById);
      return {
        id: key,
        name: `${depth ? `${'  '.repeat(depth)}↳ ` : ''}${task.title || 'Tarea sin título'}`,
        start: task.start_date,
        end: task.due_date,
        progress: normalizedProgress(task, issues),
        dependencies: hasDependencyCycle ? '' : (dependencyMap.get(key) || []).join(', '),
        // Frappe Gantt adds custom_class through DOMTokenList.add, so it must be
        // one CSS token rather than a space-separated class list.
        custom_class: `siger-gantt-task--${status}--${type}`,
        sourceTask: task,
      };
    });

  return {
    ganttTasks,
    unscheduledTasks,
    issues,
    hasDependencyCycle,
    totalTasks: tasksById.size,
  };
};
